import React, { Component } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import onClickOutside from 'react-onclickoutside'

import { UI } from '../config'
import Icon from './Icon'
import CloudDownloadOutlinedIcon from '@mui/icons-material/CloudDownloadOutlined'
import {
  clearOfflineDownloadSessionError,
  getOfflineDownloadSession,
  startOfflineDownload,
  subscribeOfflineDownloadSession,
} from '../services/offlineDownloadSession'
import { removeAllOfflineContent } from '../services/offlineRemover'
import '../styles/OfflineSaveBanner.scss'

const OFFLINE_SAVE_OPTIONS = [
  { id: 'all-content', label: UI.ALL_CONTENT },
  { id: 'owners-manual', label: UI.OWNERS_MANUAL },
]

class OfflineSaveBanner extends Component {
  static propTypes = {
    vehicleId: PropTypes.string,
    contents: PropTypes.object,
    ownersManualPdfs: PropTypes.object,
    vehicleInfo: PropTypes.object,
    downloadState: PropTypes.oneOf(['none', 'owners-manual', 'all-content']),
    onDownloadComplete: PropTypes.func,
    onOfflineChange: PropTypes.func,
  }

  static defaultProps = {
    downloadState: 'none',
  }

  state = {
    menuOpen: false,
    removing: false,
    sessionDownloading: false,
    sessionError: null,
  }

  isMountedFlag = false
  unsubscribeSession = null
  subscribedVehicleId = null
  errorClearTimeout = null

  componentDidMount() {
    this.isMountedFlag = true
    this.bindSession(this.props.vehicleId)
  }

  componentDidUpdate(prevProps) {
    if (prevProps.vehicleId !== this.props.vehicleId) {
      this.bindSession(this.props.vehicleId)
    }
  }

  componentWillUnmount() {
    this.isMountedFlag = false
    this.unbindSession()
    if (this.errorClearTimeout) {
      clearTimeout(this.errorClearTimeout)
      this.errorClearTimeout = null
    }
  }

  unbindSession = () => {
    if (this.unsubscribeSession) {
      this.unsubscribeSession()
      this.unsubscribeSession = null
    }
    this.subscribedVehicleId = null
  }

  bindSession = (vehicleId) => {
    const id = vehicleId != null && vehicleId !== '' ? String(vehicleId) : null
    if (id === this.subscribedVehicleId) {
      // Re-sync from the live session in case we remounted after navigation.
      if (id) {
        const snapshot = getOfflineDownloadSession(id)
        this.setState({
          sessionDownloading: snapshot.status === 'downloading',
          sessionError: snapshot.status === 'error' ? snapshot.error : null,
        })
      }
      return
    }

    this.unbindSession()
    if (!id) {
      this.setState({ sessionDownloading: false, sessionError: null })
      return
    }

    this.subscribedVehicleId = id
    this.unsubscribeSession = subscribeOfflineDownloadSession(id, this.handleSessionEvent)
  }

  handleSessionEvent = (event) => {
    if (!event || !this.isMountedFlag) return

    if (event.type === 'status') {
      this.setState(prevState => ({
        sessionDownloading: event.status === 'downloading',
        sessionError: event.status === 'error'
          ? event.error
          : (event.status === 'downloading' ? null : prevState.sessionError),
      }))
      return
    }

    if (event.type === 'complete') {
      this.setState({ sessionDownloading: false, sessionError: null })
      return
    }

    if (event.type === 'error') {
      this.showSessionError(event.error || 'Download failed. Please try again.')
    }
  }

  showSessionError = (message) => {
    if (this.errorClearTimeout) {
      clearTimeout(this.errorClearTimeout)
    }

    this.setState({
      sessionDownloading: false,
      sessionError: message,
    })

    this.errorClearTimeout = setTimeout(() => {
      if (this.isMountedFlag) {
        this.setState({ sessionError: null })
      }
      clearOfflineDownloadSessionError(this.props.vehicleId)
      this.errorClearTimeout = null
    }, 5000)
  }

  isDownloading = () => this.state.sessionDownloading

  isBusy = () => this.isDownloading() || this.state.removing

  toggleMenu = () => {
    if (this.isBusy()) return
    this.setState(({ menuOpen }) => ({ menuOpen: !menuOpen }))
  }

  handleClickOutside = () => {
    if (this.isBusy()) return
    this.setState({ menuOpen: false })
  }

  handleProgress = () => {}

  reportError = (message) => {
    console.error('[offline]', message)
    this.showSessionError(message)
  }

  startDownload = async(downloadType) => {
    const { vehicleId, contents, ownersManualPdfs, vehicleInfo } = this.props
    if (!vehicleId || !contents || this.isDownloading()) return

    this.setState({ menuOpen: false })

    try {
      // Session manager owns the promise across route changes; completion UI is
      // applied via session subscribers (this banner and BrowseGuideLayout).
      await startOfflineDownload({
        vehicleId,
        downloadType,
        contents,
        ownersManualPdfs,
        vehicleInfo,
        onProgress: this.handleProgress,
      })
    } catch (e) {
      // Errors are also emitted on the shared session.
    }
  }

  handleOptionClick = (optionId) => {
    this.startDownload(optionId)
  }

  handleSaveAllContent = () => {
    this.startDownload('all-content')
  }

  handleRemoveAllContent = async() => {
    const { vehicleId, onDownloadComplete } = this.props
    if (!vehicleId || this.isBusy()) return

    this.setState({ menuOpen: false, removing: true })

    try {
      await removeAllOfflineContent(vehicleId)
      if (this.isMountedFlag && onDownloadComplete) {
        onDownloadComplete('removed')
      }
    } catch (e) {
      this.reportError('Failed to remove offline content. Please try again.')
    } finally {
      if (this.isMountedFlag) {
        this.setState({ removing: false })
      }
    }
  }

  getBannerText() {
    const { downloadState } = this.props

    if (this.isDownloading()) {
      return UI.OFFLINE_BANNER_DOWNLOADING
    }
    if (downloadState === 'all-content') {
      return UI.OFFLINE_BANNER_ALL
    }
    if (downloadState === 'owners-manual') {
      return UI.OFFLINE_BANNER_PARTIAL
    }
    return UI.OFFLINE_BANNER_DEFAULT
  }

  renderAction() {
    const { downloadState } = this.props
    const { menuOpen } = this.state
    const busy = this.isBusy()

    if (downloadState === 'all-content') {
      return (
        <div className="OfflineSaveBanner-actionWrap">
          <button
            type="button"
            className="OfflineSaveBanner-action OfflineSaveBanner-action--single OfflineSaveBanner-action--outline"
            onClick={this.handleRemoveAllContent}
            disabled={busy}
          >
            {UI.REMOVE_CONTENT}
          </button>
        </div>
      )
    }

    if (downloadState === 'owners-manual') {
      return (
        <div className="OfflineSaveBanner-actionWrap">
          <button
            type="button"
            className="OfflineSaveBanner-action OfflineSaveBanner-action--single"
            onClick={this.handleSaveAllContent}
            disabled={busy}
          >
            {UI.SAVE_ALL_CONTENT}
          </button>
        </div>
      )
    }

    return (
      <div className={classNames('OfflineSaveBanner-actionWrap', menuOpen && 'is-open')}>
        <button
          type="button"
          className="OfflineSaveBanner-action"
          onClick={this.toggleMenu}
          aria-expanded={menuOpen}
          aria-haspopup="menu"
          disabled={busy}
        >
          {UI.SAVE_FOR_OFFLINE}
          <Icon type="chevron-down" />
        </button>
        {menuOpen && !busy && (
          <ul className="OfflineSaveBanner-menu" role="menu">
            {OFFLINE_SAVE_OPTIONS.map(option => (
              <li key={option.id} role="none">
                <button
                  type="button"
                  className="OfflineSaveBanner-menuItem"
                  role="menuitem"
                  onClick={() => this.handleOptionClick(option.id)}
                >
                  {option.label}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    )
  }

  render() {
    const { sessionError } = this.state
    const busy = this.isBusy()

    return (
      <div className={classNames('OfflineSaveBanner', busy && 'is-downloading')}>
        <div className="OfflineSaveBanner-content">
          <div className="OfflineSaveBanner-top">
            <div className="OfflineSaveBanner-icon">
              <CloudDownloadOutlinedIcon />
            </div>
            <p className="OfflineSaveBanner-text">{this.getBannerText()}</p>
            <div className="OfflineSaveBanner-infoWrap">
              <button
                type="button"
                className="OfflineSaveBanner-info"
                aria-label="More information"
                aria-describedby="offline-save-info-tooltip"
              >
                <Icon type="information" />
              </button>
              <span id="offline-save-info-tooltip" className="OfflineSaveBanner-tooltip" role="tooltip">
                {UI.OFFLINE_SAVE_INFO_TOOLTIP}
              </span>
            </div>
          </div>

          {this.renderAction()}
        </div>

        {sessionError && (
          <div className="OfflineSaveBanner-error">
            <span>{sessionError}</span>
          </div>
        )}
      </div>
    )
  }
}

export default onClickOutside(OfflineSaveBanner)
